import React, { useEffect, useRef, useState } from 'react'
import { Box, Typography, keyframes } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Avatar } from '@/design-system'
import { avatarGradient } from '@/features/connections/utils'
import { TopicOwner } from '@/features/album/types'
import { formatRelative } from '@/features/album/utils/date'

export interface SpotlightPick {
  id: string
  title: string
  coverUrl: string
  owner?: TopicOwner
  photoCount: number
  likes: number
  updatedAt?: string
  galleryId?: string
  onOpen: () => void
  onWalk?: () => void
}

export interface FeaturedSpotlightProps {
  picks: SpotlightPick[]
  /** ms between auto-advances. */
  intervalMs?: number
}

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`

const dotFill = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`

const Root = styled(Box)({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: '55% 1fr',
  minHeight: 330,
  borderRadius: 20,
  overflow: 'hidden',
  background: 'var(--ds-canvas)',
  boxShadow: 'var(--ds-shadow-lift)',
  '@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
})

const Ph = styled(Box)({
  position: 'relative',
  overflow: 'hidden',
  background: 'var(--ds-surface)',
  minHeight: 220,
})

const PhImg = styled('img', { shouldForwardProp: (p) => p !== '$active' })<{ $active: boolean }>(({ $active }) => ({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  opacity: $active ? 1 : 0,
  transition: 'opacity 600ms var(--ds-ease)',
}))

const WalkTag = styled(Box)({
  position: 'absolute',
  top: 14,
  left: 14,
  height: 24,
  padding: '0 11px',
  borderRadius: 'var(--ds-radius-pill)',
  background: 'rgba(13,13,15,0.6)',
  backdropFilter: 'blur(8px)',
  color: 'var(--ds-alert-warn)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.12em',
  display: 'flex',
  alignItems: 'center',
})

const Body = styled(Box)({
  position: 'relative',
  padding: '34px 36px 30px',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  color: 'var(--ds-immersive-ink)',
  // The "Generative Wall Field" treatment from
  // docs/mockups-v22/01-hero-cover-redesign.html (Option C) — brand-color
  // blobs + a fine dot grid over the same dark canvas the immersive/night
  // tokens already use elsewhere, instead of a flat light panel next to
  // the photo half.
  backgroundColor: 'var(--ds-immersive)',
  backgroundImage: `
    radial-gradient(ellipse 60% 70% at 10% 12%, rgba(255,207,125,0.55), transparent 60%),
    radial-gradient(ellipse 55% 65% at 96% 8%, rgba(254,166,125,0.4), transparent 62%),
    radial-gradient(ellipse 60% 75% at 88% 96%, rgba(190,157,253,0.48), transparent 62%),
    radial-gradient(ellipse 45% 55% at 4% 96%, rgba(1,218,254,0.35), transparent 60%),
    radial-gradient(rgba(244,243,246,0.09) 1.2px, transparent 1.2px)
  `,
  backgroundSize: 'auto, auto, auto, auto, 22px 22px',
})

const Fade = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  animation: `${fadeUp} 420ms var(--ds-ease-out) both`,
})

const Eyebrow = styled(Typography)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  background: 'linear-gradient(100deg, var(--ds-accent-deep-a), var(--ds-accent-deep-b))',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: 14,
})

const Title = styled(Typography)({
  fontSize: 'clamp(22px, 2.4vw, 31px)',
  fontWeight: 750,
  letterSpacing: '-0.03em',
  lineHeight: 1.1,
  color: 'var(--ds-immersive-ink)',
  marginBottom: 12,
})

const Tagline = styled(Typography)({
  fontSize: 14,
  lineHeight: 1.6,
  color: 'var(--ds-night-ink-muted)',
  maxWidth: 420,
  marginBottom: 18,
})

const OwnerRow = styled(Box)({ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22 })

const OwnerName = styled(Typography)({ fontSize: 13, fontWeight: 650, color: 'var(--ds-immersive-ink)' })

const Stat = styled(Box)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10,
  color: 'var(--ds-night-ink-subtle)',
  display: 'flex',
  alignItems: 'center',
  gap: 5,
})

const CtaRow = styled(Box)({ display: 'flex', alignItems: 'center', gap: 10, marginTop: 'auto', flexWrap: 'wrap' })

const SpectrumCta = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  height: 42,
  padding: '0 21px',
  borderRadius: 'var(--ds-radius-pill)',
  cursor: 'pointer',
  background: 'var(--ds-gradient-spectrum)',
  color: '#17131f',
  fontSize: 13.5,
  fontWeight: 700,
  letterSpacing: '-0.01em',
  boxShadow: '0 8px 22px -8px rgba(190,157,253,0.55)',
  transition: 'transform 140ms var(--ds-ease), box-shadow 260ms var(--ds-ease)',
  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 12px 28px -8px rgba(190,157,253,0.7)' },
  '&:active': { transform: 'scale(0.98)' },
  '& .arr': { transition: 'transform 140ms var(--ds-ease)' },
  '&:hover .arr': { transform: 'translateX(3px)' },
})

const LineCta = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  height: 42,
  padding: '0 20px',
  borderRadius: 'var(--ds-radius-pill)',
  cursor: 'pointer',
  background: 'transparent',
  color: 'var(--ds-immersive-ink)',
  fontSize: 14,
  fontWeight: 600,
  boxShadow: 'inset 0 0 0 1px rgba(244,243,246,0.25)',
  transition: 'box-shadow 140ms var(--ds-ease)',
  '&:hover': { boxShadow: 'inset 0 0 0 1px rgba(244,243,246,0.55)' },
})

const Dots = styled(Box)({ position: 'absolute', right: 30, bottom: 26, display: 'flex', gap: 6 })

const Dot = styled('button', { shouldForwardProp: (p) => p !== '$active' })<{ $active?: boolean }>(({ $active }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  width: 22,
  height: 3,
  borderRadius: 2,
  background: 'rgba(244,243,246,0.22)',
  cursor: 'pointer',
  overflow: 'hidden',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'var(--ds-gradient-spectrum)',
    transformOrigin: 'left',
    transform: $active ? undefined : 'scaleX(0)',
    animation: $active ? `${dotFill} 7000ms linear forwards` : 'none',
  },
}))

/**
 * "Editor's pick" — the top-liked album with a real cover photo, presented
 * as a light split card: crossfading cover on the left, title/owner/CTAs on
 * the right. Auto-rotates through the handful of top picks every 7s with
 * progress dots, same rotation model as the old night-theme marquee.
 */
export function FeaturedSpotlight({ picks, intervalMs = 7000 }: FeaturedSpotlightProps) {
  const [index, setIndex] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const goTo = (i: number) => {
    setIndex(((i % picks.length) + picks.length) % picks.length)
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = setInterval(() => setIndex((cur) => (cur + 1) % picks.length), intervalMs)
  }

  useEffect(() => {
    if (picks.length < 2) return undefined
    timerRef.current = setInterval(() => setIndex((cur) => (cur + 1) % picks.length), intervalMs)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picks.length, intervalMs])

  if (picks.length === 0) return null

  const pick = picks[Math.min(index, picks.length - 1)]
  const shared = formatRelative(pick.updatedAt)

  return (
    <Root>
      <Ph>
        {picks.map((p, i) => (
          <PhImg key={p.id} $active={i === index} src={p.coverUrl} alt={i === index ? p.title : ''} loading={i === 0 ? 'eager' : 'lazy'} />
        ))}
        {pick.galleryId && <WalkTag>&#9672; ALSO WALKABLE IN 3D</WalkTag>}
      </Ph>
      <Body>
        <Fade key={pick.id}>
          <Eyebrow>Editor&rsquo;s pick &mdash; this week</Eyebrow>
          <Title>{pick.title}</Title>
          <Tagline>
            {pick.photoCount} photographs{shared ? `, shared ${shared}` : ''}.
          </Tagline>
          {pick.owner && (
            <OwnerRow>
              <Avatar sx={{ width: 30, height: 30, fontSize: 12, background: avatarGradient(pick.owner.name), color: '#fff' }}>
                {pick.owner.name?.[0]?.toUpperCase()}
              </Avatar>
              <OwnerName>{pick.owner.name}</OwnerName>
              <Stat>{pick.photoCount} PHOTOS</Stat>
              <Stat>
                <span style={{ color: 'var(--ds-accent-dot-2)' }}>&#9829;</span> {pick.likes}
              </Stat>
            </OwnerRow>
          )}
          <CtaRow>
            <SpectrumCta onClick={pick.onOpen}>
              Open album <span className='arr'>&rarr;</span>
            </SpectrumCta>
            {pick.galleryId && pick.onWalk && (
              <LineCta onClick={pick.onWalk}>&#9672; Walk the 3D room</LineCta>
            )}
          </CtaRow>
        </Fade>
        {picks.length > 1 && (
          <Dots>
            {picks.map((p, i) => (
              <Dot key={p.id} $active={i === index} aria-label={`Show ${p.title}`} onClick={() => goTo(i)} />
            ))}
          </Dots>
        )}
      </Body>
    </Root>
  )
}
